/**
 * EL CAMPO — Corazón vivo del dashboard admin
 * 
 * NODOS DEL ORGANISMO:
 * - Enseñanza: La transmisión viva. Late cuando hay un ser humano real recibiendo.
 * - Comunidad: Campos que han resonado. Se mide por movimiento real.
 * - Flujo: Energía circulante — el intercambio reconocido con valor.
 * - Campus: El espacio más sagrado. Sostiene el campo desde adentro.
 * - Presencia: Hilos de conexión activos. "Quiero seguir escuchándote".
 * - Palabra: La voz en el mundo. Nodo más exterior — cuando está activo el campo se propaga.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import api from '../../lib/api';

const NODE_DEFS = [
  { key: 'ensenanza', name: 'Enseñanza', color: '#c9a84c', vi: 1,
    descripcion: 'La transmisión viva. Late cuando hay un ser humano real recibiendo algo.' },
  { key: 'comunidad', name: 'Comunidad', color: '#60a5fa', vi: 8,
    descripcion: 'Campos que han resonado con el suyo. Se mide por movimiento real.' },
  { key: 'flujo', name: 'Flujo', color: '#34d399', vi: 16,
    descripcion: 'Energía circulante — el intercambio reconocido con valor.' },
  { key: 'campus', name: 'Campus', color: '#f97316', vi: 24,
    descripcion: 'El espacio más sagrado. Sostiene el campo desde adentro.' },
  { key: 'presencia', name: 'Presencia', color: '#a78bfa', vi: 32,
    descripcion: 'Hilos de conexión activos. Personas que dijeron quiero seguir escuchándote.' },
  { key: 'palabra', name: 'Palabra', color: '#f59e0b', vi: 40,
    descripcion: 'La voz en el mundo. Cuando está activo el campo se propaga hacia afuera.' },
];

const MESES_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const mesActual = () => { const d = new Date(); return `${MESES_ES[d.getMonth()]} ${d.getFullYear()}`; };

/* ======= 3D GEODESIC SPHERE  enhanced visuals ======= */
function GeodesicSphere({ nodos }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({ rotY: 0, rotX: 0.3, dragging: false, lastX: 0, lastY: 0, zoom: 1.15, hover: -1, t: 0 });

  const generateIcosphere = useCallback(() => {
    const phi = (1 + Math.sqrt(5)) / 2;
    const raw = [[-1,phi,0],[1,phi,0],[-1,-phi,0],[1,-phi,0],[0,-1,phi],[0,1,phi],[0,-1,-phi],[0,1,-phi],[phi,0,-1],[phi,0,1],[-phi,0,-1],[-phi,0,1]];
    const verts = raw.map(v => { const l = Math.sqrt(v[0]**2+v[1]**2+v[2]**2); return [v[0]/l, v[1]/l, v[2]/l]; });
    const faces = [[0,11,5],[0,5,1],[0,1,7],[0,7,10],[0,10,11],[1,5,9],[5,11,4],[11,10,2],[10,7,6],[7,1,8],[3,9,4],[3,4,2],[3,2,6],[3,6,8],[3,8,9],[4,9,5],[2,4,11],[6,2,10],[8,6,7],[9,8,1]];
    const midCache = {};
    const getMid = (a, b) => {
      const k = Math.min(a,b)+'-'+Math.max(a,b);
      if (midCache[k] !== undefined) return midCache[k];
      const m = [(verts[a][0]+verts[b][0])/2,(verts[a][1]+verts[b][1])/2,(verts[a][2]+verts[b][2])/2];
      const l = Math.sqrt(m[0]**2+m[1]**2+m[2]**2);
      verts.push([m[0]/l,m[1]/l,m[2]/l]);
      midCache[k] = verts.length-1; return midCache[k];
    };
    let newFaces = [];
    for (const f of faces) { const a=getMid(f[0],f[1]), b=getMid(f[1],f[2]), c=getMid(f[2],f[0]); newFaces.push([f[0],a,c],[a,f[1],b],[c,b,f[2]],[a,b,c]); }
    const edges = new Set();
    for (const f of newFaces) { edges.add(`${Math.min(f[0],f[1])}-${Math.max(f[0],f[1])}`); edges.add(`${Math.min(f[1],f[2])}-${Math.max(f[1],f[2])}`); edges.add(`${Math.min(f[2],f[0])}-${Math.max(f[2],f[0])}`); }
    return { verts, edges: [...edges].map(e => e.split('-').map(Number)) };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { verts, edges } = generateIcosphere();
    const S = stateRef.current;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.clientWidth, H = canvas.clientHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    const R = Math.min(W, H) * 0.38;
    const cx = W / 2, cy = H / 2;
    const nodeVIs = [1, 8, 16, 24, 32, 40].map(v => v < verts.length ? v : 0);

    const project = (x, y, z) => { const f = 600 / (600 + z * R * S.zoom); return { sx: cx + x * R * S.zoom * f, sy: cy + y * R * S.zoom * f, f, z }; };
    const rotate = (v) => {
      let [x, y, z] = v;
      const cy2 = Math.cos(S.rotY), sy2 = Math.sin(S.rotY); [x, z] = [x*cy2-z*sy2, x*sy2+z*cy2];
      const cx2 = Math.cos(S.rotX), sx2 = Math.sin(S.rotX); [y, z] = [y*cx2-z*sx2, y*sx2+z*cx2];
      return [x, y, z];
    };

    let animId;
    const draw = () => {
      S.t += 0.016;
      if (!S.dragging) S.rotY += 0.003;
      ctx.clearRect(0, 0, W, H);

      // Edges  stronger, more defined golden wireframe
      for (const [a, b] of edges) {
        const [ax, ay, az] = rotate(verts[a]);
        const [bx, by, bz] = rotate(verts[b]);
        const pa = project(ax, ay, az), pb = project(bx, by, bz);
        const avgZ = (az + bz) / 2;
        const alpha = Math.max(0.1, Math.min(0.45, (1 - avgZ / 2) * 0.38));
        ctx.beginPath(); ctx.moveTo(pa.sx, pa.sy); ctx.lineTo(pb.sx, pb.sy);
        ctx.strokeStyle = `rgba(185,148,70,${alpha})`; ctx.lineWidth = 0.9; ctx.stroke();
      }

      // Free vertices (membrana viva — 36 points of golden light, pulsing harmonically)
      for (let i = 0; i < verts.length; i++) {
        if (nodeVIs.includes(i)) continue;
        const [x, y, z] = rotate(verts[i]);
        const p = project(x, y, z);
        const depthAlpha = Math.max(0.32, (1 - z / 2) * 0.75);
        const pulse = Math.sin(S.t * 0.4 + i * 0.17) * 0.22 + 0.78; // 0.56..1.0 harmonic wave
        const alpha = depthAlpha * pulse;
        const r = 3.4 * p.f; // ~half of department nodes (6.5)
        // Soft golden glow
        const grd = ctx.createRadialGradient(p.sx, p.sy, 0, p.sx, p.sy, r * 3);
        grd.addColorStop(0, `rgba(232,212,144,${alpha * 0.55})`);
        grd.addColorStop(1, 'rgba(232,212,144,0)');
        ctx.beginPath(); ctx.arc(p.sx, p.sy, r * 3, 0, Math.PI * 2);
        ctx.fillStyle = grd; ctx.fill();
        // Core dot — warm gold
        ctx.beginPath(); ctx.arc(p.sx, p.sy, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(232,212,144,${alpha})`; ctx.fill();
      }

      // Department nodes  larger, more glow, more presence
      for (let ni = 0; ni < NODE_DEFS.length; ni++) {
        const vi = nodeVIs[ni];
        if (vi >= verts.length) continue;
        const nd = nodos?.find(n => n.key === NODE_DEFS[ni].key);
        const st = nd?.state || 'reposo';
        const [x, y, z] = rotate(verts[vi]);
        const p = project(x, y, z);
        const baseAlpha = st === 'activo' ? 1.0 : st === 'gestando' ? 0.75 : 0.45;
        const pulse = Math.sin(S.t * 1.5 + ni) * 0.1;
        const r = (6.5 + pulse * 6) * p.f * S.zoom;
        const col = NODE_DEFS[ni].color;

        // Outer glow  stronger
        const grd = ctx.createRadialGradient(p.sx, p.sy, 0, p.sx, p.sy, r * 3.5);
        grd.addColorStop(0, col + Math.round(baseAlpha * 55).toString(16).padStart(2, '0'));
        grd.addColorStop(1, col + '00');
        ctx.beginPath(); ctx.arc(p.sx, p.sy, r * 3.5, 0, Math.PI * 2);
        ctx.fillStyle = grd; ctx.fill();

        // Core dot
        ctx.beginPath(); ctx.arc(p.sx, p.sy, r, 0, Math.PI * 2);
        ctx.fillStyle = col; ctx.globalAlpha = baseAlpha; ctx.fill(); ctx.globalAlpha = 1;

        // White highlight on top  glass effect
        ctx.beginPath(); ctx.arc(p.sx - r * 0.25, p.sy - r * 0.25, r * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${baseAlpha * 0.3})`; ctx.fill();

        // Label  bolder, more visible
        if (p.f > 0.55 && z < 0.6) {
          ctx.font = '600 11px system-ui, sans-serif';
          ctx.fillStyle = `rgba(42,32,16,${Math.max(0.5, baseAlpha * 0.9)})`;
          ctx.textAlign = 'center';
          ctx.fillText(NODE_DEFS[ni].name, p.sx, p.sy + r + 16);
        }
      }

      // Central node  always visible, pulsing, with concentric rings
      const cp = project(0, 0, 0);
      const cPulse = Math.sin(S.t * 1.7) * 0.25 + 0.75;
      const cR = 7 * cPulse;
      for (let ring = 3; ring >= 1; ring--) {
        const rr = cR + ring * 7;
        const ringPulse = Math.sin(S.t * 1.2 + ring * 0.5) * 0.03 + 0.07;
        ctx.beginPath(); ctx.arc(cp.sx, cp.sy, rr, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(201,168,76,${ringPulse * (4 - ring)})`; ctx.lineWidth = 1.2; ctx.stroke();
      }
      ctx.beginPath(); ctx.arc(cp.sx, cp.sy, cR, 0, Math.PI * 2);
      const cGrd = ctx.createRadialGradient(cp.sx - 1, cp.sy - 1, 0, cp.sx, cp.sy, cR);
      cGrd.addColorStop(0, '#e8d490'); cGrd.addColorStop(1, '#c9a84c');
      ctx.fillStyle = cGrd; ctx.fill();

      animId = requestAnimationFrame(draw);
    };
    draw();

    const onDown = (e) => { S.dragging = true; const r = canvas.getBoundingClientRect(); S.lastX = (e.touches?.[0]?.clientX || e.clientX) - r.left; S.lastY = (e.touches?.[0]?.clientY || e.clientY) - r.top; };
    const onMove = (e) => { if (!S.dragging) return; const r = canvas.getBoundingClientRect(); const mx = (e.touches?.[0]?.clientX || e.clientX) - r.left; const my = (e.touches?.[0]?.clientY || e.clientY) - r.top; S.rotY += (mx - S.lastX) * 0.008; S.rotX += (my - S.lastY) * 0.008; S.lastX = mx; S.lastY = my; };
    const onUp = () => { S.dragging = false; };
    const onWheel = (e) => { e.preventDefault(); S.zoom = Math.max(0.6, Math.min(1.6, S.zoom - e.deltaY * 0.001)); };

    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseup', onUp);
    canvas.addEventListener('mouseleave', onUp);
    canvas.addEventListener('touchstart', onDown, { passive: true });
    canvas.addEventListener('touchmove', onMove, { passive: true });
    canvas.addEventListener('touchend', onUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    return () => { cancelAnimationFrame(animId); canvas.removeEventListener('mousedown', onDown); canvas.removeEventListener('mousemove', onMove); canvas.removeEventListener('mouseup', onUp); canvas.removeEventListener('mouseleave', onUp); canvas.removeEventListener('touchstart', onDown); canvas.removeEventListener('touchmove', onMove); canvas.removeEventListener('touchend', onUp); canvas.removeEventListener('wheel', onWheel); };
  }, [nodos, generateIcosphere]);

  return <canvas ref={canvasRef} className="w-full" style={{ height: '420px', cursor: 'grab' }} data-testid="geodesic-sphere" />;
}

/* ======= EDITABLE TEXT CARD  enhanced ======= */
function EditableCard({ label, texto, placeholder, hint, onSave, badge, className = '' }) {
  const [val, setVal] = useState(texto || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(texto || ''); }, [texto]);
  const handleBlur = async () => { if (val === texto) return; setSaving(true); await onSave(val); setSaving(false); };
  return (
    <div className={`bg-[#fdf8ee] border border-[#c9a84c55] rounded-[14px] p-5 shadow-sm ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-[0.1em] text-[#9a7a30] font-sans font-semibold">{label}</span>
        <div className="flex items-center gap-2">
          {badge && <span className="text-[10px] bg-[#c9a84c20] text-[#7a6020] px-2.5 py-0.5 rounded-full font-sans font-medium">{badge}</span>}
          {saving && <span className="text-[10px] text-[#c9a84c] font-sans animate-pulse">guardando...</span>}
        </div>
      </div>
      <textarea
        value={val} onChange={e => setVal(e.target.value)} onBlur={handleBlur}
        placeholder={placeholder} rows={2}
        className="w-full bg-transparent text-[15px] text-[#2a2010] font-serif italic resize-none border-none outline-none placeholder:text-[#b0a08060] leading-relaxed"
        data-testid={`campo-${label.toLowerCase().replace(/ /g, '-')}`}
      />
      {hint && <div className="text-[10px] text-[#b09060] font-sans mt-1.5">{typeof hint === 'string' ? hint : hint}</div>}
    </div>
  );
}

/* ======= HISTORY MODAL ======= */
function HistorialModal({ title, items, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-[14px] border border-[#d4c8a8] w-full max-w-md max-h-[70vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e8dfc8] bg-[#fdf8ee]">
          <span className="text-sm font-sans font-semibold text-[#2a2010]">{title}</span>
          <button onClick={onClose} className="text-[#a09070] hover:text-[#5a4a2a] transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto max-h-[55vh] p-4 space-y-3">
          {items.length === 0 ? (
            <p className="text-sm text-[#a09070] text-center py-8 font-sans">Sin registros anteriores</p>
          ) : items.map((item, i) => (
            <div key={i} className="bg-[#fdf8ee] rounded-xl p-4 border border-[#c9a84c30]">
              <span className="text-[10px] text-[#9a7a30] font-sans font-semibold uppercase tracking-wider">{item.mes}</span>
              <p className="text-[14px] text-[#2a2010] font-serif italic mt-2 leading-relaxed">{item.texto || ''}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ======= STATE BADGE  more defined ======= */
const StateBadge = ({ state }) => {
  const styles = {
    activo: 'bg-[#c9a84c25] text-[#7a5a10] border border-[#c9a84c40]',
    reposo: 'bg-[#eee8da] text-[#8a7a60] border border-[#d8d0b8]',
    gestando: 'bg-[#e0ecff] text-[#3060b0] border border-[#c0d4f0]',
  };
  return <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-sans font-medium ${styles[state] || styles.reposo}`}>{state}</span>;
};

/* ======= COHERENCE PHRASES ======= */
const getSentidoPhrase = (v) => {
  if (v <= 20) return 'El campo pide pausa y escucha';
  if (v <= 40) return 'Algo se está reorganizando';
  if (v <= 60) return 'Movimiento consciente';
  if (v <= 80) return 'Flujo con dirección clara';
  return 'Campo alineado y en expansión';
};
const getAlignPhrase = (sentido, sistema) => {
  const diff = Math.abs(sentido - sistema);
  if (diff <= 12) return 'Sistema y sentido alineados';
  if (sentido > sistema + 12) return 'Sientes más que muestran los datos';
  return 'Los datos superan lo que sientes';
};

/* ======= MAIN PAGE ======= */
export default function AdminCampo() {
  const [nodos, setNodos] = useState([]);
  const [coherenciaSistema, setCoherenciaSistema] = useState(0);
  const [frecuencia, setFrecuencia] = useState('');
  const [presencia, setPresencia] = useState('');
  const [intencion, setIntencion] = useState('');
  const [sentido, setSentido] = useState(50);
  const [historial, setHistorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [frecuenciasCampo, setFrecuenciasCampo] = useState([]);
  const [frecuenciasAbiertas, setFrecuenciasAbiertas] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [nodosR, freqR, presR, intR, cohR, fcR] = await Promise.all([
          api.get('/admin/campo/nodos'),
          api.get('/admin/campo/frecuencia'),
          api.get('/admin/campo/presencia'),
          api.get('/admin/campo/intencion'),
          api.get('/admin/campo/coherencia'),
          api.get('/admin/campo/frecuencias-campo'),
        ]);
        setNodos(nodosR.data.nodos || []);
        setCoherenciaSistema(nodosR.data.coherencia_sistema || 0);
        setFrecuencia(freqR.data.texto || '');
        setPresencia(presR.data.texto || '');
        setIntencion(intR.data.texto || '');
        setSentido(cohR.data.valor_sentido ?? 50);
        setFrecuenciasCampo(fcR.data.niveles || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const saveFrecuencia = (t) => api.put('/admin/campo/frecuencia', { texto: t });
  const savePresencia = (t) => api.put('/admin/campo/presencia', { texto: t });
  const saveIntencion = (t) => api.put('/admin/campo/intencion', { texto: t });
  const saveSentido = (v) => { setSentido(v); api.put('/admin/campo/coherencia', { valor_sentido: v }); };
  const openHistorial = async (type) => {
    const r = await api.get(`/admin/campo/${type}/historial`);
    setHistorial({ type: type === 'presencia' ? 'Presencias anteriores' : 'Intenciones anteriores', items: r.data || [] });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-[#c9a84c] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#f8f5ee' }} data-testid="admin-campo">
      {/* Header  stronger, more presence */}
      <div className="px-6 pt-7 pb-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-3 h-3 rounded-full bg-[#c9a84c] shadow-[0_0_8px_rgba(201,168,76,0.4)]" />
          <h1 className="text-3xl font-serif text-[#2a2010] tracking-tight">El Campo</h1>
        </div>
        <p className="text-sm text-[#9a7a30] font-sans font-medium tracking-wide ml-6">{mesActual()} &middot; Corazón vivo de la escuela</p>
        <p className="text-[13px] italic text-[#8a7a60] tracking-wide ml-6 mt-1" style={{ fontFamily: 'Georgia, serif' }}>
          El espacio donde la escuela se contempla a sí misma
        </p>
        <div className="mt-4 h-[1px] bg-gradient-to-r from-[#c9a84c50] via-[#c9a84c20] to-transparent" />
      </div>

      {/* Two-column layout */}
      <div className="px-6 pb-10 flex flex-col lg:flex-row gap-5">
        {/* LEFT COLUMN */}
        <div className="lg:w-[62%] space-y-4">
          <EditableCard
            label="Frecuencia Esencial"
            texto={frecuencia}
            placeholder="Escribe aquí la esencia inmutable de tu escuela..."
            hint="Se edita solo cuando algo fundamental cambia"
            onSave={saveFrecuencia}
          />

          {/* Geodesic Sphere  larger, more defined container */}
          <div className="bg-white border border-[#d4c8a8] rounded-[14px] p-2 shadow-sm" style={{ minHeight: '440px' }}>
            <GeodesicSphere nodos={nodos} />
          </div>

          <EditableCard
            label="Presencia Ahora"
            texto={presencia}
            placeholder="¿Qué sostiene el campo este mes?"
            onSave={savePresencia}
            badge={mesActual()}
            hint={<button onClick={() => openHistorial('presencia')} className="text-[#9a7a30] hover:text-[#7a5a10] hover:underline text-[11px] font-medium transition-colors">Ver presencias anteriores</button>}
          />
        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:w-[38%] space-y-4">
          {/* Nodos del Organismo  more weight */}
          <div className="bg-white border border-[#d4c8a8] rounded-[14px] p-5 shadow-sm">
            <span className="text-[11px] uppercase tracking-[0.1em] text-[#9a7a30] font-sans font-semibold">Nodos del organismo</span>
            <div className="mt-4 space-y-3.5">
              {nodos.map(n => (
                <div key={n.key} className="flex items-center gap-3.5" data-testid={`nodo-${n.key}`}>
                  <div className="w-3.5 h-3.5 rounded-full flex-shrink-0 shadow-sm" style={{
                    backgroundColor: n.color,
                    opacity: n.state === 'activo' ? 1 : n.state === 'gestando' ? 0.7 : 0.35,
                    boxShadow: n.state === 'activo' ? `0 0 8px ${n.color}50` : 'none'
                  }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5">
                      <span className="text-[13px] font-sans font-semibold text-[#2a2010]">{n.name}</span>
                      <StateBadge state={n.state} />
                    </div>
                    <p className="text-[11px] text-[#6a5a4a] font-sans mt-0.5">{n.stat}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Intención del Ciclo */}
          <div className="bg-white border border-[#d4c8a8] rounded-[14px] p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] uppercase tracking-[0.1em] text-[#9a7a30] font-sans font-semibold">Intención del ciclo</span>
              <span className="text-[10px] bg-[#c9a84c20] text-[#7a5a10] px-2.5 py-0.5 rounded-full font-sans font-medium">{mesActual()}</span>
            </div>
            <textarea
              value={intencion} onChange={e => setIntencion(e.target.value)} onBlur={() => saveIntencion(intencion)}
              placeholder="¿Qué intención guía este ciclo?"
              rows={4}
              className="w-full bg-[#fdf8ee] text-[14px] text-[#2a2010] font-serif italic resize-none border border-[#c9a84c30] rounded-xl p-3.5 outline-none placeholder:text-[#b0a08060] leading-relaxed"
              data-testid="campo-intencion"
            />
            <button onClick={() => openHistorial('intencion')} className="text-[11px] text-[#9a7a30] hover:text-[#7a5a10] hover:underline mt-1.5 font-medium transition-colors">Ver ciclos anteriores</button>
          </div>

          {/* Coherencia del Campo  more visual weight */}
          <div className="bg-white border border-[#d4c8a8] rounded-[14px] p-5 shadow-sm">
            <span className="text-[11px] uppercase tracking-[0.1em] text-[#9a7a30] font-sans font-semibold">Coherencia del campo</span>

            <div className="mt-5 space-y-4">
              {/* Sistema bar  thicker */}
              <div>
                <div className="flex justify-between text-[12px] font-sans mb-1.5">
                  <span className="text-[#5a4a3a] font-medium">Sistema</span>
                  <span className="text-[#c9a84c] font-bold">{coherenciaSistema}</span>
                </div>
                <div className="h-3 bg-[#ede4d0] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${coherenciaSistema}%`, background: 'linear-gradient(90deg, #d4b860, #c9a84c)' }} />
                </div>
              </div>

              {/* Sentido bar + slider */}
              <div>
                <div className="flex justify-between text-[12px] font-sans mb-1.5">
                  <span className="text-[#5a4a3a] font-medium">Sentido</span>
                  <span className="text-[#7a9cc8] font-bold">{sentido}</span>
                </div>
                <div className="h-3 bg-[#ede4d0] rounded-full overflow-hidden mb-2.5">
                  <div className="h-full rounded-full transition-all" style={{ width: `${sentido}%`, background: 'linear-gradient(90deg, #90b8e0, #7aa0d0)' }} />
                </div>
                <input type="range" min="0" max="100" value={sentido}
                  onChange={e => setSentido(Number(e.target.value))}
                  onMouseUp={e => saveSentido(Number(e.target.value))}
                  onTouchEnd={e => saveSentido(Number(e.target.value))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: '#c9a84c' }}
                  data-testid="campo-sentido-slider"
                />
              </div>
            </div>

            {/* Big number + phrases  more presence */}
            <div className="mt-5 pt-4 border-t border-[#ede4d0] text-center">
              <span className="text-[28px] font-sans font-light text-[#c9a84c]" data-testid="campo-sentido-value">{sentido}</span>
              <p className="text-[12px] text-[#5a4a3a] font-sans font-medium mt-1">{getSentidoPhrase(sentido)}</p>
              <p className="text-[11px] text-[#8a7a60] font-sans mt-0.5 italic">{getAlignPhrase(sentido, coherenciaSistema)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Las 36 frecuencias del campo — invitación contemplativa */}
      <div className="px-6 pb-16">
        <div className="h-[0.5px] bg-[#e0d4b0] mx-auto max-w-md mb-6" />
        <div className="text-center">
          <button
            onClick={() => setFrecuenciasAbiertas(v => !v)}
            className="text-[14px] tracking-wide hover:text-[#8a6c30] transition-colors"
            style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#b09050' }}
            data-testid="toggle-frecuencias-campo"
          >
            {frecuenciasAbiertas ? 'Cerrar las 36 frecuencias' : 'Las 36 frecuencias del campo'}
          </button>
        </div>

        {frecuenciasAbiertas && (
          <div
            className="mt-8 mx-auto max-w-6xl"
            style={{
              background: '#fdf9f0',
              borderTop: '0.5px solid #e0d4b0',
              borderRadius: '14px',
              padding: '32px 36px',
            }}
            data-testid="frecuencias-campo-panel"
          >
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '18px', color: '#2a2010', margin: 0 }}>
              Las 36 frecuencias del campo
            </h2>
            <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '13px', color: '#9a8a6a', marginTop: '6px', marginBottom: '28px' }}>
              Leyes vivas que sostienen y recalibran el organismo
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              {frecuenciasCampo.map(({ nivel, items }) => {
                const color = items[0]?.color_nivel || '#b09050';
                const nivelTitulo = {
                  1: 'Nivel 1 — Críticos',
                  2: 'Nivel 2 — Estructura',
                  3: 'Nivel 3 — Expansión',
                  4: 'Nivel 4 — Integración',
                  5: 'Nivel 5 — Sistema sutil',
                  6: 'Nivel 6 — Campo',
                }[nivel];
                return (
                  <div key={nivel} data-testid={`nivel-${nivel}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-[11px] uppercase tracking-[0.12em] font-sans font-semibold" style={{ color }}>
                        {nivelTitulo}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {items.map(f => (
                        <div key={f.numero} className="flex gap-3">
                          <span
                            className="text-[11px] font-sans font-semibold flex-shrink-0 w-5 text-right pt-[2px]"
                            style={{ color }}
                          >
                            {String(f.numero).padStart(2, '0')}
                          </span>
                          <div className="flex-1">
                            <div className="text-[13px] font-sans text-[#2a2010]" style={{ fontWeight: 500 }}>
                              {f.titulo}
                            </div>
                            <p
                              className="text-[13px] text-[#5a4a3a] mt-0.5 leading-snug"
                              style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
                            >
                              {f.frecuencia}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {historial && <HistorialModal title={historial.type} items={historial.items} onClose={() => setHistorial(null)} />}
    </div>
  );
}
